import * as THREE from 'three';
import { BaseInputController } from './BaseInputController';
import { GameScene } from '../scenes/GameScene';
import { BasketballDimensions } from '../../../shared/constants/BasketballDimensions';


import { Player } from '../models/Player';
import { Debug, LogCategory } from '@shared/Debug';

export class TouchInputController extends BaseInputController {
  private mousePosition: THREE.Vector2 = new THREE.Vector2();
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private targetPosition: THREE.Vector3 | null = null;
  private readonly ARRIVAL_THRESHOLD = 2; // Distance in meters to consider "arrived"
  private readonly MOVE_SPEED = 3.5;
  private isDragging: boolean = false;
  private lastTouchPosition: THREE.Vector2 = new THREE.Vector2();
  private touchStartPosition: THREE.Vector2 = new THREE.Vector2();
  private touchStartTime: number = 0;
  private readonly TAP_THRESHOLD = 200; // ms to consider a touch a tap vs drag
  private readonly DRAG_DISTANCE_THRESHOLD = 10; // pixels to consider a touch a drag
  private readonly MAX_JOYSTICK_DISTANCE = 100; // Maximum distance in pixels for joystick input
  private readonly MAX_MOVE_SPEED = 3; // Maximum movement speed
  private readonly MIN_MOVE_SPEED = 1; // Minimum movement speed
  private readonly MOVEMENT_EPSILON = 0.01; // Threshold for movement change
  private hasExceededDragThreshold: boolean = false;
  private currentJoystickOffset: THREE.Vector2 = new THREE.Vector2();
  private lastSentMovement: THREE.Vector2 = new THREE.Vector2();
  private backBoardHitA: THREE.Mesh | null = null;
  private backBoardHitB: THREE.Mesh | null = null;
  private lastTargetedPlayer: Player | null = null;

  private boundMouseMove: (event: MouseEvent) => void;
  private boundMouseDown: (event: MouseEvent) => void;
  private boundMouseUp: (event: MouseEvent) => void;
  private boundTouchStart: (event: TouchEvent) => void;
  private boundTouchMove: (event: TouchEvent) => void;
  private boundTouchEnd: (event: TouchEvent) => void;
  private boundTouchCancel: (event: TouchEvent) => void;

  constructor(camera: THREE.PerspectiveCamera, gameScene: GameScene) {
    super(camera, gameScene);
    
    // Bind event handlers
    this.boundMouseMove = this.handleMouseMove.bind(this);
    this.boundMouseDown = this.handleMouseDown.bind(this);
    this.boundMouseUp = this.handleMouseUp.bind(this);
    this.boundTouchStart = this.handleTouchStart.bind(this);
    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.boundTouchEnd = this.handleTouchEnd.bind(this);
    this.boundTouchCancel = this.handleTouchCancel.bind(this);
    
    this.initTouchListeners();
    this.initBackboardHitAreas();
  }

  private initTouchListeners(): void {
    // Handle mouse events
    window.addEventListener('mousemove', this.boundMouseMove);
    window.addEventListener('mousedown', this.boundMouseDown);
    window.addEventListener('mouseup', this.boundMouseUp);
    
    // Handle touch events
    window.addEventListener('touchstart', this.boundTouchStart, { passive: false });
    window.addEventListener('touchmove', this.boundTouchMove, { passive: false });
    window.addEventListener('touchend', this.boundTouchEnd, { passive: false });
    window.addEventListener('touchcancel', this.boundTouchCancel, { passive: false });
  }

  private handleMouseMove(event: MouseEvent): void {
    this.mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    if (this.isDragging) {
        const dx = event.clientX - this.touchStartPosition.x;
        const dy = event.clientY - this.touchStartPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance > this.DRAG_DISTANCE_THRESHOLD) {
            if (!this.hasExceededDragThreshold) {
                this.hasExceededDragThreshold = true;
                // Only clear targeting if we're not targeting a player for a pass
                if (!this.gameScene.playerHasBall() || !this.lastTargetedPlayer) {
                    Debug.info('Drag detected, clearing targeting since not passing', LogCategory.PLAYER);
                    this.clearTargeting();
                }
            }
            
            if (this.hasExceededDragThreshold) {
                this.handleDragMove(event.clientX, event.clientY);
            }
        }
        
        // Update pass targeting regardless of drag state when we have the ball
        if (this.gameScene.playerHasBall()) {
            this.handlePassTargeting();
        }
    }
  }

  private handleMouseDown(event: MouseEvent): void {
    Debug.info('Mouse down event started', LogCategory.PLAYER);
    // Check if click is on a UI element
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('#ui-container')) {
        Debug.info('Click on UI element, ignoring', LogCategory.PLAYER);
        return;
    }

    this.isDragging = true;
    this.hasExceededDragThreshold = false;
    this.touchStartPosition.x = event.clientX;
    this.touchStartPosition.y = event.clientY;
    this.lastTouchPosition.x = event.clientX;
    this.lastTouchPosition.y = event.clientY;
    this.touchStartTime = Date.now();

    // Update mouse position and raycaster for targeting
    this.mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(event.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.camera);

    // Start targeting if we have the ball
    if (this.gameScene.playerHasBall()) {
        Debug.info('Player has ball, starting pass targeting', LogCategory.PLAYER);
        this.handlePassTargeting();
    }
  }

  private handleMouseUp(event: MouseEvent): void {
    Debug.info('Mouse up event started', LogCategory.PLAYER);
    
    // Check if click is on a UI element
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('#ui-container')) {
        Debug.info('Click on UI element, ignoring', LogCategory.PLAYER);
        return;
    }

    // Update mouse position and raycaster before handling click
    this.mousePosition.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(event.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.camera);

    // Check if this was a tap/click (short duration, minimal movement) or a drag
    const dx = event.clientX - this.touchStartPosition.x;
    const dy = event.clientY - this.touchStartPosition.y;
    const moveDistance = Math.sqrt(dx * dx + dy * dy);
    
    // Store the targeted player before we clear anything
    const targetedPlayer = this.lastTargetedPlayer;
    const hasValidTarget = targetedPlayer && typeof targetedPlayer.getPosition === 'function';
    Debug.info(`Has ball: ${this.gameScene.playerHasBall()}, Has target: ${hasValidTarget}, Move distance: ${moveDistance}`, LogCategory.PLAYER);

    // If we have a valid pass target and haven't moved much, execute the pass
    if (hasValidTarget && this.gameScene.playerHasBall() && moveDistance <= this.DRAG_DISTANCE_THRESHOLD) {
        Debug.info('Attempting pass to targeted player', LogCategory.PLAYER);
        const targetPos = targetedPlayer.getPosition();
        this.handlePlayerClick(targetPos);
    } else if (!hasValidTarget && !this.hasExceededDragThreshold && moveDistance <= this.DRAG_DISTANCE_THRESHOLD) {
        Debug.info('No valid pass target and was a click, handling as movement click', LogCategory.PLAYER);
        // Only handle as movement click if it wasn't a drag
        this.handleClick();
    }

    // Reset drag state
    this.isDragging = false;
    this.hasExceededDragThreshold = false;
    this.currentJoystickOffset.set(0, 0);
    this.gameScene.movePlayer(0, 0);
    this.lastSentMovement.set(0, 0);
    
    // Clear target position if we were dragging
    if (this.hasExceededDragThreshold) {
        this.targetPosition = null;
    }

    // Clear targeting after attempting pass
    this.clearTargeting();
  }

  private handleTouchStart(event: TouchEvent): void {
    event.preventDefault();
    const touch = event.touches[0];
    
    // Check if touch is on a UI element
    const target = touch.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('#ui-container')) {
        return;
    }
    
    this.isDragging = true;
    this.hasExceededDragThreshold = false;
    this.touchStartPosition.x = touch.clientX;
    this.touchStartPosition.y = touch.clientY;
    this.lastTouchPosition.x = touch.clientX;
    this.lastTouchPosition.y = touch.clientY;
    this.touchStartTime = Date.now();
    
    // Update mouse position and raycaster for targeting
    this.mousePosition.x = (touch.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(touch.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.camera);

    // Start targeting if we have the ball
    if (this.gameScene.playerHasBall()) {
        this.handlePassTargeting();
    }
  }

  private handleTouchMove(event: TouchEvent): void {
    event.preventDefault();
    const touch = event.touches[0];
    
    this.mousePosition.x = (touch.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(touch.clientY / window.innerHeight) * 2 + 1;
    
    if (this.isDragging) {
        if (!this.hasExceededDragThreshold) {
            const dx = touch.clientX - this.touchStartPosition.x;
            const dy = touch.clientY - this.touchStartPosition.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance > this.DRAG_DISTANCE_THRESHOLD) {
                this.hasExceededDragThreshold = true;
                this.clearTargeting(); // Clear targeting if we start dragging
            }
        }
        
        if (this.hasExceededDragThreshold) {
            this.handleDragMove(touch.clientX, touch.clientY);
        } else if (this.gameScene.playerHasBall()) {
            // Only update targeting if we haven't exceeded drag threshold
            this.handlePassTargeting();
        }
    }
  }

  private handleTouchEnd(event: TouchEvent): void {
    event.preventDefault();
    
    // Get the last known touch position for the click handling
    const lastTouch = event.changedTouches[0];
    this.mousePosition.x = (lastTouch.clientX / window.innerWidth) * 2 - 1;
    this.mousePosition.y = -(lastTouch.clientY / window.innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mousePosition, this.camera);

    // Check if this was a tap (short duration, minimal movement) or a drag
    const touchDuration = Date.now() - this.touchStartTime;
    const dx = lastTouch.clientX - this.touchStartPosition.x;
    const dy = lastTouch.clientY - this.touchStartPosition.y;
    const moveDistance = Math.sqrt(dx * dx + dy * dy);
    
    const wasTap = touchDuration < this.TAP_THRESHOLD && moveDistance < this.DRAG_DISTANCE_THRESHOLD;

    if (wasTap) {
        // If we have a targeted player and the ball, pass to them
        if (this.lastTargetedPlayer && this.gameScene.playerHasBall()) {
            const targetPos = this.lastTargetedPlayer.getPosition();
            this.handlePlayerClick(targetPos);
        } else {
            // Only handle as movement click if it wasn't a drag
            this.handleClick();
        }
    }

    // Reset drag state
    this.isDragging = false;
    this.hasExceededDragThreshold = false;
    this.currentJoystickOffset.set(0, 0);
    this.gameScene.movePlayer(0, 0);
    this.lastSentMovement.set(0, 0);
    
    // Clear target position if we were dragging
    if (this.hasExceededDragThreshold) {
        this.targetPosition = null;
    }

    // Always clear targeting on touch end
    this.clearTargeting();
  }

  private handleTouchCancel(event: TouchEvent): void {
    event.preventDefault();
    if (this.isDragging) {
        this.isDragging = false;
        this.hasExceededDragThreshold = false;
        this.currentJoystickOffset.set(0, 0);
        this.gameScene.movePlayer(0, 0);
        this.lastSentMovement.set(0, 0);
    }

    // Clear targeting
    this.clearTargeting();
  }

  private handleDragMove(currentX: number, currentY: number): void {
    // Calculate joystick offset from start position
    const dx = currentX - this.touchStartPosition.x;
    const dy = currentY - this.touchStartPosition.y;
    
    // Store the current offset for use in update()
    this.currentJoystickOffset.set(dx, dy);
  }

  private initBackboardHitAreas(): void {
    const hoopAPosition = this.gameScene.getHoopAPosition();
    const hoopBPosition = this.gameScene.getHoopBPosition();

    if (!hoopAPosition || !hoopBPosition) return;
    
    const hitAreaHeight = 6;
    const hitAreaWidth = 10;
    this.backBoardHitA = new THREE.Mesh(
      new THREE.BoxGeometry(hitAreaWidth, hitAreaHeight, 0.01),
      new THREE.MeshBasicMaterial({ color: 0xFF0000, transparent: true, opacity: 0 })
    );
    this.backBoardHitA.rotation.set(0, -Math.PI / 2, 0);
    this.backBoardHitA.position.set(hoopAPosition.x - BasketballDimensions.rim.distanceFromBackboard, hoopAPosition.y + hitAreaHeight / 2, hoopAPosition.z);
    this.gameScene.getScene().add(this.backBoardHitA);

    this.backBoardHitB = new THREE.Mesh(
      new THREE.BoxGeometry(hitAreaWidth, hitAreaHeight, 0.01),
      new THREE.MeshBasicMaterial({ color: 0x0000FF, transparent: true, opacity: 0 })
    );
    this.backBoardHitB.rotation.set(0, -Math.PI / 2, 0);
    this.backBoardHitB.position.set(hoopBPosition.x + BasketballDimensions.rim.distanceFromBackboard, hoopBPosition.y + hitAreaHeight / 2, hoopBPosition.z);
    this.gameScene.getScene().add(this.backBoardHitB);
  }

  private async handleClick(): Promise<void> {
    if (!this.backBoardHitA || !this.backBoardHitB) {
      this.initBackboardHitAreas();
    }

    // Check if the click is on a backboard
    const hits = this.raycaster.intersectObjects([this.backBoardHitA, this.backBoardHitB]);
    
    if (hits.length > 0) {
      if (hits.some(hit => hit.object === this.backBoardHitA)) {
        this.handleBackboardClick('A');
      } else if (hits.some(hit => hit.object === this.backBoardHitB)) {
        this.handleBackboardClick('B');
      }
      this.clearTargeting();
      return;
    }

    // Handle court click for movement
    const courtPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const intersectionPoint = new THREE.Vector3();
    const didIntersect = this.raycaster.ray.intersectPlane(courtPlane, intersectionPoint);
    
    if (!didIntersect) {
      this.clearTargeting();
      return;
    }

    // Get court dimensions
    const courtDimensions = this.gameScene.getCourtDimensions();
    if (!courtDimensions) {
      this.clearTargeting();
      return;
    }

    // Check if click is within court bounds
    if (Math.abs(intersectionPoint.x) > courtDimensions.width * 1.5 / 2 ||
        Math.abs(intersectionPoint.z) > courtDimensions.length * 1.5 / 2) {
      this.clearTargeting();
      return;
    }

    this.targetPosition = intersectionPoint.clone(); // Set the target position for movement
    this.clearTargeting();
  }

  private handleBackboardClick(backboardId: 'A' | 'B'): void {
    
    const playerPosition = this.gameScene.getPlayerPosition();
    if (!playerPosition) return;

    // Get the target basket based on which backboard was clicked
    const targetBasket = backboardId === 'A' ? 
      this.gameScene.getHoopAPosition() : 
      this.gameScene.getHoopBPosition();

    if (!targetBasket) return;

    if (!this.gameScene.playerHasBall()) {
      this.gameScene.processPlayerBlock();
      return;
    }

    // Shoot after a short delay to allow rotation to complete
    setTimeout(() => {
      this.gameScene.processPlayerShoot(targetBasket.clone());
    }, 100);
  }

  private handlePlayerClick(passTarget: THREE.Vector3): void {
    Debug.info(`handlePlayerClick called with target: ${JSON.stringify(passTarget)}`, LogCategory.PLAYER);
    if (this.gameScene.playerHasBall()) {
        Debug.info('Player has ball, executing pass', LogCategory.PLAYER);
        this.gameScene.processPlayerPass(passTarget.x, passTarget.z);
    } else {
        Debug.warn('handlePlayerClick called but player does not have ball', LogCategory.PLAYER);
    }
  }

  public update(_: number): void {
    // Handle movement to target position if it exists
    if (this.targetPosition && !this.isDragging) {
      const playerPosition = this.gameScene.getPlayerPosition();
      if (playerPosition) {
        const dx = this.targetPosition.x - playerPosition.x;
        const dz = this.targetPosition.z - playerPosition.z;
        const distanceToTarget = Math.sqrt(dx * dx + dz * dz);

        if (distanceToTarget > this.ARRIVAL_THRESHOLD) {
          // Calculate normalized direction
          const dirX = dx / distanceToTarget;
          const dirZ = dz / distanceToTarget;
          
          // Move player towards target
          this.gameScene.movePlayer(dirX * this.MOVE_SPEED, dirZ * this.MOVE_SPEED);
          this.lastSentMovement.set(dirX * this.MOVE_SPEED, dirZ * this.MOVE_SPEED);
        } else {
          // We've arrived at the target
          this.targetPosition = null;
          this.gameScene.movePlayer(0, 0);
          this.lastSentMovement.set(0, 0);
        }
      }
    } else if (this.isDragging) {
      // Clear target position when dragging starts
      this.targetPosition = null;
      
      // Calculate the normalized direction and magnitude
      const dx = this.currentJoystickOffset.x;
      const dy = this.currentJoystickOffset.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Only move if we exceed the initial drag threshold
      if (distance > this.DRAG_DISTANCE_THRESHOLD) {
        // Calculate the movement speed based on drag distance
        const speedFactor = Math.min(distance / this.MAX_JOYSTICK_DISTANCE, 1.0);
        const speed = this.MIN_MOVE_SPEED + (this.MAX_MOVE_SPEED - this.MIN_MOVE_SPEED) * speedFactor;
        
        // Normalize the direction
        const normalizedX = dx / distance;
        const normalizedY = dy / distance;
        
        // Calculate final movement
        const moveSpeed = speed;
        
        // Check if movement has changed enough to send update
        const deltaX = Math.abs(normalizedX * moveSpeed - this.lastSentMovement.x);
        const deltaY = Math.abs(normalizedY * moveSpeed - this.lastSentMovement.y);
        
        if (deltaX > this.MOVEMENT_EPSILON || deltaY > this.MOVEMENT_EPSILON) {
          const newX = normalizedX * moveSpeed;
          const newY = normalizedY * moveSpeed;
          this.gameScene.movePlayer(newX, newY);
          this.lastSentMovement.set(newX, newY);
        }
      } else {
        // If under threshold and we weren't already stopped, stop movement
        if (this.lastSentMovement.x !== 0 || this.lastSentMovement.y !== 0) {
          this.gameScene.movePlayer(0, 0);
          this.lastSentMovement.set(0, 0);
        }
      }
    } else if (this.lastSentMovement.x !== 0 || this.lastSentMovement.y !== 0) {
      // Not dragging and we weren't already stopped, stop movement
      this.gameScene.movePlayer(0, 0);
      this.lastSentMovement.set(0, 0);
    }
  }

  public dispose(): void {
    // Remove event listeners using the bound functions
    window.removeEventListener('mousemove', this.boundMouseMove);
    window.removeEventListener('mousedown', this.boundMouseDown);
    window.removeEventListener('mouseup', this.boundMouseUp);
    window.removeEventListener('touchstart', this.boundTouchStart);
    window.removeEventListener('touchmove', this.boundTouchMove);
    window.removeEventListener('touchend', this.boundTouchEnd);
    window.removeEventListener('touchcancel', this.boundTouchCancel);

    // Clean up targeting
    this.clearTargeting();
  }

  private handlePassTargeting(): void {
    Debug.info('handlePassTargeting started', LogCategory.PLAYER);
    // Only show targeting if we have the ball
    if (!this.gameScene.playerHasBall()) {
      Debug.info('No ball, clearing targeting', LogCategory.PLAYER);
      this.clearTargeting();
      return;
    }

    this.raycaster.setFromCamera(this.mousePosition, this.camera);
    const intersects = this.raycaster.intersectObjects(this.gameScene.getPlayerMeshes(), true);

    // Clear previous targeting if we're not intersecting with any player
    if (intersects.length === 0) {
      Debug.info('No player intersected, clearing targeting', LogCategory.PLAYER);
      this.clearTargeting();
      return;
    }

    // Find the first player mesh that was intersected
    const intersectedMesh = intersects[0].object;
    const targetedPlayer = this.gameScene.getPlayerByMesh(intersectedMesh);

    Debug.info(`Found intersected player: ${!!targetedPlayer}, Is local: ${targetedPlayer?.isLocalPlayer()}`, LogCategory.PLAYER);

    // If we found a player and it's different from the last targeted player
    // and it's not the local player and it's a valid player object
    if (targetedPlayer && 
        targetedPlayer !== this.lastTargetedPlayer && 
        !targetedPlayer.isLocalPlayer() &&
        typeof targetedPlayer.getPosition === 'function') { // Validate it's a proper Player object
      Debug.info('Setting new target player', LogCategory.PLAYER);
      // Hide ring on previously targeted player
      if (this.lastTargetedPlayer && typeof this.lastTargetedPlayer.hideLocalPlayerRing === 'function') {
        this.lastTargetedPlayer.hideLocalPlayerRing();
      }

      // Show ring on newly targeted player
      targetedPlayer.showLocalPlayerRing();
      this.lastTargetedPlayer = targetedPlayer;
    }
  }

  private clearTargeting(): void {
    Debug.info('Clearing targeting', LogCategory.PLAYER);
    // Hide ring on last targeted player
    if (this.lastTargetedPlayer && typeof this.lastTargetedPlayer.hideLocalPlayerRing === 'function') {
      this.lastTargetedPlayer.hideLocalPlayerRing();
      this.lastTargetedPlayer = null;
    }
  }
} 